import type { ReactNode } from 'react';
import {
    lazy,
    Suspense,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { CSSProperties } from 'react';
import { Play } from 'lucide-react';
import {
    useLoaderData,
    useLocation,
    useNavigation,
    useSearchParams,
} from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { Image } from '@rocco/components/image';
import { useTracker } from '@archipro-website/tracker';
import {
    GALLERY_SLIDE_GUTTER_REM,
    GALLERY_SLIDE_HEIGHT_REM,
    GALLERY_SLIDE_PAD_REM,
    GALLERY_VIEWPORT_REM,
    PRODUCT_GALLERY_SLIDE_SIZES,
    galleryTrackOffset,
    getSlideRatio,
    GALLERY_SLIDE_WIDTH_MOBILE_VW,
} from './gallery-geometry';
import { useBoundedRail } from './use-gallery-rail';
import getGallerySlides from '@modules/product/util/get-gallery-slides';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import { isContainedSlide } from '@modules/product/util/is-contained-slide';
import { getThumbnailImage } from '@modules/product/util/get-thumbnail-image';
import type {
    LibraryDiagram,
    LibraryImage,
    LibraryVideo,
} from '@modules/product/type/product-gallery-types';
import { cn } from '@rocco/utils/cn';
import VideoSlide from '@modules/product/component/photo-gallery/VideoSlide';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';
import { BoostActionButton } from '@modules/page-builder/component/common/upscale-warning/BoostActionButton';
import { useMatchesHandleData } from '~/modules/root/hook';
import { useFeature } from '@modules/root';
import {
    useIslandSection,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
// Direct, not via the barrel: the barrel also pulls in TileCarousel, which
// imports back into this tree.
import { useCarouselArrowKeys } from '~/ui-rocco/pages/detail-shared/use-carousel-arrow-keys';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import { CarouselPaginationPill } from '~/ui-rocco/pages/home-page/components/CarouselPaginationPill';
import { useProductDetailHeroStyle } from '@modules/product/util/product-hero-view-transition';
import { getPendingHeroImage } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';

/** Lazy-loaded photo viewer — keeps the heavy lightbox out of the page chunk. */
const DetailImageOverlay = lazy(() =>
    import('~/ui-rocco/pages/detail-shared/DetailImageOverlay').then((m) => ({
        default: m.DetailImageOverlay,
    })),
);

/** Autoplay cadence — the duration of the island's progress fill / dwell per slide. */
const GALLERY_AUTOPLAY_DELAY = 4000;
/**
 * How long a slide takes to travel when nothing is holding it — autoplay, the
 * island's dots, the arrow keys. Apple's product rail runs its advances at
 * 983 / 967 / 1000 / 983ms, measured frame by frame; a round second sits inside
 * that spread. The curve matters more than the number and lives in `RailMotion`.
 */
const GALLERY_ADVANCE_MS = 1000;

/**
 * Play gallery videos inline inside the fullscreen viewer. The carousel shows
 * a poster only; playback is delegated to DetailImageOverlay.
 */
const GALLERY_VIDEO_PLAYS_INLINE = true;

/**
 * The slack a measurement must clear before the row counts as fitting.
 *
 * The offset above resolves the same condition in CSS, against a fractional
 * `100%`; a measurement here is a rounded pixel count. Demanding a clear pixel
 * keeps the measured answer strictly more conservative than the CSS one, so the
 * two can never disagree in the direction that matters — the carousel stood
 * down while CSS still holds the row off to one side, with nothing able to move
 * it. It also keeps the clamp above saturated: a whole pixel of slack scales
 * past the inset of any row narrow enough to fit.
 */
const GALLERY_FIT_SLACK_PX = 1;

/** Whether a row of `trackRem` fits across `widthPx`, with the slack above. */
const rowFits = (trackRem: number, remPx: number, widthPx: number): boolean =>
    trackRem * remPx + GALLERY_FIT_SLACK_PX <= widthPx;

/**
 * Whether the desktop row fits across the viewport, answered during render.
 * Measured off the document because the carousel has not been laid out yet;
 * false on the server, where there is no viewport to compare against.
 */
const rowFitsAtFirstRender = (trackRem: number): boolean => {
    if (typeof document === 'undefined') return false;
    if (!window.matchMedia('(min-width: 768px)').matches) return false;
    const root = document.documentElement;
    const remPx = parseFloat(getComputedStyle(root).fontSize);
    return rowFits(trackRem, remPx, root.clientWidth);
};

/**
 * The stable `/images/s1/…/file.ext` portion of a rendition URL — tile and
 * gallery renditions of the same photo differ by CDN prefix and transform
 * suffix, so equality is only meaningful on this segment.
 */
const galleryImageKey = (url: string): string | null =>
    url.match(/\/images\/s1\/.+?\.(?:jpe?g|png|webp|gif)/i)?.[0] ?? null;

/**
 * The photograph a still is, if it is one. A diagram is a drawing of the
 * product rather than a picture of its own, so it has none.
 */
const getPhotoID = (slide: LibraryImage | LibraryDiagram): number | undefined =>
    slide.__typename === 'DiagramImage' ? undefined : slide.ID;

/**
 * The height a slide's image is served at: the card's, doubled for high
 * density screens. Boost Resolution brings a photograph up to it.
 */
const SLIDE_RENDITION_HEIGHT = GALLERY_SLIDE_HEIGHT_REM * 16 * 2;

/**
 * Boost Resolution for a photograph in the carousel, sized to the rendition
 * the slide is served at. Videos and diagrams have no photograph of their own
 * to upscale, so they go without.
 */
const SlideBoost = ({
    slide,
    disabled,
    onAutoplayPause,
    children,
}: {
    slide: LibraryImage | LibraryDiagram | LibraryVideo;
    disabled: boolean;
    onAutoplayPause: (paused: boolean) => void;
    children: ReactNode;
}) => {
    if (
        disabled ||
        isLibraryVideo(slide) ||
        slide.__typename === 'DiagramImage'
    ) {
        return <>{children}</>;
    }
    const src = slide.LibraryImageFile?.DetailPageNatural?.Desktop;
    if (!src) return <>{children}</>;

    return (
        <BoostResolutionOverlay
            libraryImageId={slide.ID}
            currentImageUrl={src}
            displayWidth={Math.round(
                SLIDE_RENDITION_HEIGHT * getSlideRatio(slide),
            )}
            displayHeight={SLIDE_RENDITION_HEIGHT}
            wasUpscaled={Boolean(slide.WasUpscaled)}
            onAutoplayPause={onAutoplayPause}
            className="absolute inset-0"
            // The slide's action row draws the trigger, through
            // BoostActionButton.
            controlInActionPanel
        >
            {children}
        </BoostResolutionOverlay>
    );
};

/** What saving a still pins: the photograph, or else the product. */
const getSlidePin = (
    slide: LibraryImage | LibraryDiagram,
    productID: number,
) => {
    const photoID = getPhotoID(slide);
    return photoID
        ? { pinId: photoID, pinType: 'PhotoPin' as const }
        : { pinId: productID, pinType: 'ProductPin' as const };
};

/** True when the Magic Island is active (flag on, or dev `?detailsRevamp=1`). */
const useIslandEnabled = () => {
    const { detailsRevamp } = useLoaderData<typeof loader>();
    const [searchParams] = useSearchParams();
    return (
        detailsRevamp ||
        (import.meta.env.DEV && searchParams.get('detailsRevamp') === '1')
    );
};

const GalleryPagination = ({
    trackFits,
    total,
    activeIndex,
}: {
    trackFits: boolean;
    /**
     * Counted in original slides. The carousel's own `totalSlides` counts
     * embla's snaps, which include the copies the row is repeated into so the
     * loop holds — the reader should never be told there are six images when
     * the product has three.
     */
    total: number;
    activeIndex: number;
}) => {
    const { api, scrollPrev, scrollNext, canScrollPrev, canScrollNext } =
        useCarousel();

    // When the Magic Island is on it owns the pagination, so the in-page pill
    // hides — otherwise the gallery would show two pagination controls.
    if (useIslandEnabled()) return null;

    // A row that fits the viewport shows every slide at once — the carousel is
    // switched off there, so pagination would step through nothing.
    if (trackFits) return null;

    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center max-md:pt-3 md:pt-[3rem]">
            <CarouselPaginationPill
                total={total}
                activeIndex={activeIndex}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Product gallery"
            />
        </div>
    );
};

/**
 * Carousel video tile — poster and play mark only. A tap opens the same
 * fullscreen viewer a photograph does; playback stays out of the carousel row.
 */
const GalleryVideoSlideTrigger = ({
    slide,
    productTitle,
    index,
    isCentred,
    onActivate,
    loading,
}: {
    slide: LibraryVideo;
    productTitle: string;
    index: number;
    isCentred: boolean;
    onActivate: () => void;
    loading?: 'eager' | 'lazy';
}) => {
    const { image } = getThumbnailImage(slide);
    const src = image.Desktop ?? '';

    return (
        <button
            type="button"
            onClick={onActivate}
            // A pointer, never the magnifier the photo tiles use: a video
            // opens a player, and nothing about it is magnified.
            className="absolute inset-0 cursor-pointer"
            aria-label={
                isCentred
                    ? `View ${productTitle} video ${index + 1} full screen`
                    : `Bring ${productTitle} video ${index + 1} into view`
            }
        >
            <Image
                src={src}
                alt={slide.Title || productTitle}
                fill
                fit="cover"
                sizes={PRODUCT_GALLERY_SLIDE_SIZES}
                loading={loading ?? 'lazy'}
                fadeIn={false}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
            >
                <div className="flex size-16 items-center justify-center rounded-full bg-black/40 md:size-24">
                    <Play
                        className="size-6 fill-white text-white md:size-9"
                        strokeWidth={0}
                    />
                </div>
            </div>
        </button>
    );
};

/**
 * Fullscreen viewer video slide with an always-visible play indicator. The
 * indicator needs `z-10` because the aria VideoPlayer's internals stack above
 * plain siblings, and it hides while the video plays (VideoSlide reports
 * playback via `onPlayingChange`). `pointer-events-none` keeps clicks flowing
 * to the player.
 */
const GalleryVideoSlide = ({ slide }: { slide: LibraryVideo }) => {
    const [playing, setPlaying] = useState(false);

    return (
        <div className="absolute inset-0">
            <VideoSlide
                libraryVideo={slide}
                onPlayingChange={setPlaying}
                hideNativePlayIcon
                playsInline={GALLERY_VIDEO_PLAYS_INLINE}
            />
            <div
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-200',
                    playing ? 'opacity-0' : 'opacity-100',
                )}
            >
                <div className="flex size-16 items-center justify-center rounded-full bg-black/40 md:size-24">
                    <Play
                        className="size-6 fill-white text-white md:size-9"
                        strokeWidth={0}
                    />
                </div>
            </div>
        </div>
    );
};

/**
 * F3 — editorial gallery.
 *
 * Large centre-aligned carousel with adjacent peeks, mirroring the homepage
 * Discover Projects banner treatment. The slide stack matches the legacy
 * gallery — images + videos (sorted by member order) then diagrams, via
 * `getGallerySlides`. Tapping an image or a video logs `PhotoViewerTrigger`
 * and opens the lightbox at that slide, at every width (kept in sync with the
 * carousel both ways).
 * Award badges overlay the gallery behind the `award_badges` flag. Hidden when
 * the product has no media.
 */
export const ProductGallerySection = () => {
    const { product } = useLoaderData<typeof loader>();
    const { ctas } = usePageCtas();
    const tracker = useTracker();
    // One viewer at every width: the lightbox below. Mobile only differs in
    // opening it straight from a tap, with no click-to-centre first.
    const { mobile } = useAppDisplayModeContext();
    const islandEnabled = useIslandEnabled();
    const boundedRail = useBoundedRail();
    // Slide 0 is the counterpart of the listing tile the user clicked, so it
    // carries the shared snapshot name for the arriving transition.
    const heroStyle = useProductDetailHeroStyle();
    // The tile rendition the user clicked (already decoded) — painted under
    // slide 0's own image so a cold cache shows the same pixels sharpening
    // instead of a grey placeholder. Latched at mount; null on full loads.
    const { pathname } = useLocation();
    const [heroUnderlaySrc, setHeroUnderlaySrc] = useState(() =>
        getPendingHeroImage(pathname),
    );
    // The underlay is the tile's SQUARE crop; covered into a landscape slide
    // box it reads noticeably zoomed-in, so the real rendition dissolves in
    // over it on load instead of popping to the wider framing.
    const [heroImageLoaded, setHeroImageLoaded] = useState(false);
    const markHeroImageLoaded = useCallback(() => setHeroImageLoaded(true), []);

    const [viewerOpen, setViewerOpen] = useState(false);
    // A slide being boosted, or compared after one: the carousel holds still
    // so the slide stays under the reader for the before/after.
    const [boostHold, setBoostHold] = useState(false);
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);

    // Track the settled slide so the neighbours of wherever the user scrolls
    // to flip from lazy to eager before they peek into view.
    const [currentSlide, setCurrentSlide] = useState(0);
    // Whether autoplay has stopped on the last slide (so the island shows a
    // replay control). Derived from the same live embla snap count `advance`
    // uses, so it can't disagree with the stop logic — the dot index
    // (`stack.length`) and embla's snap count can differ (e.g. mobile
    // `trimSnaps`), which would otherwise strand the carousel stopped with a
    // non-functional play/pause button. Only `advance` sets it true; settling
    // off the last slide (drag-back, dot nav) clears it.
    const [reachedEnd, setReachedEnd] = useState(false);

    const enableAwardBadges = useFeature('award_badges');

    // Diagrams close the row after the photographs and videos. The Specs &
    // Dimensions frame shows them as well.
    const rawStack = getGallerySlides({
        LibraryImages: product.LibraryImages,
        LibraryVideos: product.LibraryVideos,
        DiagramImages: product.DiagramImages ?? [],
    });
    // Open the carousel on the image the user clicked: tile grids and the
    // gallery order their images independently, so the clicked image is often
    // not the gallery's first slide — the morph would land on it and then
    // dissolve to a different photo. ROTATE the stack (not reorder — the
    // carousel loops, so cyclic order is unchanged) so slide 0, which carries
    // the morph target, underlay and dissolve, IS the clicked image.
    const heroKey = heroUnderlaySrc ? galleryImageKey(heroUnderlaySrc) : null;
    const heroSlideIndex = heroKey
        ? rawStack.findIndex(
              (slide) =>
                  !isLibraryVideo(slide) &&
                  galleryImageKey(
                      slide.LibraryImageFile?.DetailPageNatural?.Desktop ?? '',
                  ) === heroKey,
          )
        : -1;
    const stack =
        heroSlideIndex > 0
            ? [
                  ...rawStack.slice(heroSlideIndex),
                  ...rawStack.slice(0, heroSlideIndex),
              ]
            : rawStack;

    // Slide 0's width (height × its clamped ratio, plus the gutters the
    // CarouselItem carries), so the centred hero is pre-positioned at SSR and
    // doesn't jump when embla hydrates. Embla centres the padded slide, so the
    // resting offset has to measure the same box — otherwise the row shifts by
    // half a gutter the moment embla takes over.
    const firstSlide = stack[0];
    const firstSlideRatio = firstSlide ? getSlideRatio(firstSlide) : 1;
    const firstSlideWidthRem =
        GALLERY_SLIDE_HEIGHT_REM * firstSlideRatio + GALLERY_SLIDE_GUTTER_REM;
    // Every slide's card plus its gutters, and the widest single member of the
    // row — the one that decides whether embla's loop survives.
    const slideWidthsRem = stack.map(
        (slide) =>
            GALLERY_SLIDE_HEIGHT_REM * getSlideRatio(slide) +
            GALLERY_SLIDE_GUTTER_REM,
    );
    const baseTrackRem = slideWidthsRem.reduce((total, w) => total + w, 0);
    const widestSlideRem = slideWidthsRem.length
        ? Math.max(...slideWidthsRem)
        : 0;
    // How many times the row is laid down, so the loop the design asks for is
    // the loop embla actually runs.
    //
    // Embla silently drops `loop: true` unless it can spare any one slide from
    // the row without uncovering the viewport (`canLoop()`:
    // `track - widestSlide >= viewport`). Two square gallery slides are 92% of
    // the viewport at every desktop width — the root font-size is vw-anchored —
    // so a two- or three-slide gallery never clears it, the loop is thrown
    // away, and the centred first slide rests against a slide-width of empty
    // track. Repeating the row until it clears is what fills that space with
    // the wrapped last image instead.
    //
    // Measured against the design frame rather than the live viewport because
    // this decides how many slides are in the DOM, and that has to be the same
    // number on the server and the client. Below the 16px cap the frame IS the
    // viewport (the root font-size is 0.833vw, so 100vw is always 120rem); a
    // window wider than 1920 can hold a row this misses, which costs it a
    // carousel where a static centred row would have done.
    //
    // The bounded rail does not wrap, so it takes the row once: repeating it
    // there would just show the reader the same images twice over.
    const loopRepeats =
        !boundedRail && stack.length > 1 && baseTrackRem > GALLERY_VIEWPORT_REM
            ? Math.max(
                  1,
                  Math.ceil(
                      (GALLERY_VIEWPORT_REM + widestSlideRem) / baseTrackRem,
                  ),
              )
            : 1;
    // What the DOM gets. Every copy is a real slide to embla — the pagination
    // and the autoplay's stop both count in originals, not in these.
    const renderedStack =
        loopRepeats > 1
            ? Array.from({ length: loopRepeats }, () => stack).flat()
            : stack;
    const trackWidthRem = baseTrackRem * loopRepeats;
    // `currentSlide` counts embla's snaps, which include the repeated copies.
    // Everything the reader sees counts originals: the dots, the island's
    // position, and where autoplay stops.
    const realSlideCount = stack.length;
    const realCurrentSlide = realSlideCount ? currentSlide % realSlideCount : 0;

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => {
            setCurrentSlide(emblaApi.selectedScrollSnap());
            // Modulo the originals: with the row repeated, embla's own last
            // snap is the end of the LAST copy, which the reader never reaches.
            if (
                emblaApi.selectedScrollSnap() % Math.max(1, stack.length) <
                Math.max(1, stack.length) - 1
            ) {
                setReachedEnd(false);
            }
        };
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
        return () => {
            emblaApi.off('select', onSelect);
            emblaApi.off('reInit', onSelect);
        };
    }, [emblaApi, stack.length]);

    // Whether the whole desktop row fits across the viewport. Compared in
    // pixels because the root font-size is viewport-relative, so the row's rem
    // width is not a fixed number of them. Deliberately not measured off the
    // DOM: a looping embla shunts individual slides out past the row's real
    // extent, and a measurement that moved with those shunts could deactivate
    // the carousel that produced it — and reactivate on the next measure.
    const [trackFits, setTrackFits] = useState(false);
    // The same answer, but available during render. It drives only embla's
    // `active`, which never reaches the DOM — unlike `trackFits`, which labels
    // the slides and so has to start out matching the server. Embla parks the
    // active slide the instant it initialises, a frame before the effect below
    // could stand it down, and that frame is a visible lurch on a row that was
    // already centred. Read off the document rather than the carousel because
    // the row has not been laid out yet; the effect replaces it with the real
    // measurement, so a resize is still answered correctly.
    //
    // Re-seeded whenever the row changes width, because the section is not
    // remounted between products: navigating from a long gallery to a short one
    // would otherwise carry the old answer until the effect caught up, which is
    // the very lurch this is here to prevent.
    const [carouselIdle, setCarouselIdle] = useState(() =>
        rowFitsAtFirstRender(trackWidthRem),
    );
    const [seededForTrackWidth, setSeededForTrackWidth] =
        useState(trackWidthRem);
    if (seededForTrackWidth !== trackWidthRem) {
        setSeededForTrackWidth(trackWidthRem);
        setCarouselIdle(rowFitsAtFirstRender(trackWidthRem));
    }
    useEffect(() => {
        const root = emblaApi?.rootNode();
        if (!root) return;
        const measure = () => {
            // Mobile lays the row out in vw, not the rem width above, and its
            // rows always overflow — the desktop row is the only one to check.
            const desktop = window.matchMedia('(min-width: 768px)').matches;
            const remPx = parseFloat(
                getComputedStyle(document.documentElement).fontSize,
            );
            // The fractional width, matching what the CSS offset compares
            // against — `clientWidth` rounds, and a rounded-up pixel is exactly
            // the disagreement the slack exists to rule out.
            const fits =
                desktop &&
                rowFits(
                    trackWidthRem,
                    remPx,
                    root.getBoundingClientRect().width,
                );
            setTrackFits(fits);
            setCarouselIdle(fits);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(root);
        return () => observer.disconnect();
    }, [emblaApi, trackWidthRem]);

    // Drive the Magic Island's pagination mode from the gallery's own carousel.
    // The section owns the embla api (via setApi), so it registers directly
    // rather than through useIslandCarousel — the in-view ref then sits on the
    // full-height <section>. Single-image galleries register nothing.
    // The autoplay clock lives here, not in the island's progress fill — so
    // scrolling (which unmounts the island, and any in-flight CSS fill with it)
    // can't drop a pending advance or reset the dwell, which is what made the
    // carousel stick while the island flickered. The island's fill is a visual
    // indicator only; `advance` runs on the timer below. `remainingDwellRef`
    // carries the dwell left on the current slide so pause/resume stays in step.
    const navigation = useNavigation();
    const [isPlaying, setIsPlaying] = useState(true);
    const remainingDwellRef = useRef(GALLERY_AUTOPLAY_DELAY);
    // The autoplay clock only runs while the island is actually on screen — the
    // island reports its mount/unmount here (and stays silent under reduced
    // motion). The banked dwell means the flicker during scroll resumes, not
    // resets.
    const [islandActive, setIslandActive] = useState(false);
    const handleIslandVisible = useCallback(
        (visible: boolean) => setIslandActive(visible),
        [],
    );

    /**
     * Everything above that belongs to the PRODUCT rather than to the section,
     * re-seeded when the reader moves to another one.
     *
     * The section is not remounted between products — the same fact the row's
     * own re-seed above is written for. Without this the state latched on the
     * way in is the state the next product inherits: the underlay keeps
     * painting the rendition clicked two products ago, the carousel opens
     * wherever the last one was left, and autoplay stays stopped because the
     * previous gallery reached its end.
     *
     * Seeded during render rather than in an effect so the first paint of the
     * new product is already right, which is the same reason `carouselIdle`
     * above is.
     */
    const [seededForPathname, setSeededForPathname] = useState(pathname);
    if (seededForPathname !== pathname) {
        setSeededForPathname(pathname);
        // Null unless this navigation started from a tile, which is exactly
        // when there is no underlay to paint.
        setHeroUnderlaySrc(getPendingHeroImage(pathname));
        setHeroImageLoaded(false);
        setCurrentSlide(0);
        setReachedEnd(false);
        setIsPlaying(true);
        remainingDwellRef.current = GALLERY_AUTOPLAY_DELAY;
    }
    // The carousel's own position is embla's, not React's, so it takes an
    // imperative jump. Layout effect, not effect: it has to land before the
    // browser paints, or the new product shows for a frame at the old one's
    // scroll offset.
    useLayoutEffect(() => {
        emblaApi?.scrollTo(0, true);
    }, [emblaApi, pathname]);
    const togglePlay = useCallback(
        () => setIsPlaying((playing) => !playing),
        [],
    );
    const advance = useCallback(() => {
        if (!emblaApi) return;
        // Stop on the last slide instead of looping back, so the island swaps
        // its play/pause control for a replay button (see PaginationIslandContent).
        // Read the snap count live from embla (not a captured stack.length) so
        // client-side navigation between products can't leave a stale length that
        // makes a shorter gallery loop past its end.
        // The last ORIGINAL slide, not embla's last snap: the row may be laid
        // down more than once so the loop holds, and autoplay must stop where
        // the gallery ends rather than run the reader through it twice.
        const count = Math.max(1, stack.length);
        if (emblaApi.selectedScrollSnap() % count >= count - 1) {
            setIsPlaying(false);
            setReachedEnd(true);
            return;
        }
        emblaApi.scrollNext();
    }, [emblaApi, stack.length]);
    const replay = useCallback(() => {
        if (!emblaApi) return;
        setReachedEnd(false);
        setIsPlaying(true);
        emblaApi.scrollTo(0);
    }, [emblaApi]);

    // A row that fits has nothing to step through, and the binding swallows the
    // arrow keys' own default while it is on screen — so hand it nothing.
    useCarouselArrowKeys(trackFits ? null : emblaApi);

    // A fresh slide gets the full dwell. (Declared before the timer effect so
    // its reset wins over the timer's elapsed-bookkeeping on a slide change.)
    useEffect(() => {
        remainingDwellRef.current = GALLERY_AUTOPLAY_DELAY;
    }, [currentSlide]);
    // Dragging the gallery pauses autoplay — a drag mid-dwell must not have
    // the timer yank the carousel to the next slide out from under the hand.
    // Keyed on actual movement while the pointer is down, NOT bare pointerDown:
    // that fires for plain clicks too (click-to-zoom opens the photo viewer)
    // and would silently stop the show on a common action. Deliberately does
    // not auto-resume; the island's play control restarts it.
    useEffect(() => {
        if (!emblaApi) return;
        let pointerIsDown = false;
        const onDown = () => {
            pointerIsDown = true;
        };
        const onUp = () => {
            pointerIsDown = false;
        };
        const onScroll = () => {
            if (pointerIsDown) setIsPlaying(false);
        };
        emblaApi.on('pointerDown', onDown);
        emblaApi.on('pointerUp', onUp);
        emblaApi.on('scroll', onScroll);
        return () => {
            emblaApi.off('pointerDown', onDown);
            emblaApi.off('pointerUp', onUp);
            emblaApi.off('scroll', onScroll);
        };
    }, [emblaApi]);
    // The autoplay timer: advance after the remaining dwell while playing and
    // not yet ended; on pause/unschedule, bank the elapsed so resume continues
    // from where it left off (matching the island's frozen fill).
    //
    // It also holds while a navigation is in flight. Pressing Back (or any
    // link) leaves a beat before the next route paints, and a gallery that
    // advances inside that beat reads as "the slider moved instead of the
    // page" — the visible answer to a click should never be the wrong one.
    //
    // It holds while the fullscreen viewer is open for the same reason: the
    // reader is looking at the viewer, and a carousel that keeps stepping
    // behind it lands them somewhere else in the gallery on close. The dwell
    // is banked by the cleanup below, so closing resumes rather than restarts.
    useEffect(() => {
        if (!emblaApi || !isPlaying || reachedEnd || !islandActive) return;
        if (viewerOpen || boostHold) return;
        if (navigation.state !== 'idle') return;
        const startedAt = performance.now();
        const timer = window.setTimeout(advance, remainingDwellRef.current);
        return () => {
            window.clearTimeout(timer);
            remainingDwellRef.current = Math.max(
                0,
                remainingDwellRef.current - (performance.now() - startedAt),
            );
        };
    }, [
        emblaApi,
        isPlaying,
        reachedEnd,
        islandActive,
        viewerOpen,
        boostHold,
        currentSlide,
        advance,
        navigation.state,
    ]);

    const islandMode = useMemo<IslandMode>(
        () => ({
            kind: 'pagination',
            label: 'Product gallery',
            total: stack.length,
            activeIndex: realCurrentSlide,
            onSelect: (index) => emblaApi?.scrollTo(index),
            isPlaying,
            onTogglePlay: togglePlay,
            autoplayDelayMs: GALLERY_AUTOPLAY_DELAY,
            onReplay: replay,
            atEnd: reachedEnd,
            onVisible: handleIslandVisible,
        }),
        [
            stack.length,
            realCurrentSlide,
            emblaApi,
            isPlaying,
            togglePlay,
            replay,
            reachedEnd,
            handleIslandVisible,
        ],
    );
    const islandRef = useIslandSection<HTMLElement>(islandMode, {
        // Desktop only: the island's gallery pagination drives the centred
        // desktop carousel. Mobile is a plain swipe row (the island pagination
        // was unreliable there), so it registers nothing and the purchase pill
        // stays the island's mobile content.
        // A row that fits the viewport shows every slide at once, so there is
        // nothing for the island to page through (and, with the island gone,
        // nothing driving the autoplay clock either).
        enabled: !mobile && stack.length > 1 && !trackFits,
        // Above link/cta default (0) so gallery pagination sticks while peeking.
        priority: 1,
    });

    if (stack.length === 0) return null;

    const isMulti = stack.length > 1;
    // A lone clear-cut (multiplied) landscape/square image extends to the page
    // content gutter as one wide band: the product stays contained at natural
    // size and the neutral fill spans both gutters. Not a proportional upscale,
    // and no carousel needed. Portraits keep the standard centred card.
    const singleClearCutWide =
        !isMulti &&
        !!firstSlide &&
        !isLibraryVideo(firstSlide) &&
        isContainedSlide(firstSlide) &&
        firstSlideRatio >= 1;
    /**
     * The page gutter the row scrolls inside.
     *
     * Mobile always takes the 1rem page gutter, and a lone slide stretches to
     * fill the width inside it (see the CarouselItem width) rather than
     * centring at its ratio width — a narrow floating card with deep side
     * gutters reads unbalanced, especially in app-mode where the top pad
     * matches this gutter.
     *
     * The bounded rail takes a gutter on desktop too, because that is what its
     * first and last slides come to rest on: the 3rem the rest of the page
     * uses, less the padding the slide carries on that side, so the gutter
     * lands on the edge of the image rather than on the padding around it. The
     * looping row has no end to rest anywhere, and keeps the full width.
     *
     * Classes rather than an inline value on both counts: a custom property
     * cannot be responsive on its own, and an inline one would outrank the
     * media query.
     */
    const galleryGutter = cn(
        '[--g-pad:1rem]',
        boundedRail && 'md:[--g-pad:calc(3rem_-_var(--g-slide-pad))]',
    );

    const trackViewerOpen = () => {
        tracker.log('PhotoViewerTrigger', {
            url: new URL(window.location.href),
            data: { LibraryItemID: product.ID || 0 },
        });
    };

    /**
     * The stack for the overlay — photographs and videos share one ordered list
     * so each entry keeps the index it came from and the carousel can be mapped
     * back and forth.
     *
     * Derived rather than memoised: this sits past an early return, where a
     * hook would change the hook order between renders.
     */
    /**
     * The photographs' own hover panel — Enquire, Visit site, More — so the
     * gallery behaves as every other image on the page does. They carried Save
     * alone.
     *
     * The enquiry is about the product the photograph belongs to, and the site
     * is its supplier's; a photo has neither of its own. It drops out with the
     * page's Enquire button.
     */
    const galleryProfessional = product.Professional;
    const photoEnquiry = ctas.enquire.visible
        ? {
              itemType: 'product' as const,
              siteTreeID: product.ID,
              professionalId: galleryProfessional.ID,
              professionalName: galleryProfessional.Title,
              professionalLink: galleryProfessional.Link,
              itemTitle: product.Title,
          }
        : null;
    const photoVisitWebsite = galleryProfessional.Website
        ? {
              website: galleryProfessional.Website,
              libraryItemID: product.ID,
              professionalID: galleryProfessional.ID,
              professionalTitle: galleryProfessional.Title,
          }
        : null;
    const photoShare = {
        url: product.Link,
        title: product.Title,
    };
    const photoTileInfo = {
        title: product.Title,
        subtitle: galleryProfessional.Title,
        imageShape: 'square' as const,
    };

    const overlaySlides: Array<{
        src: string;
        alt: string;
        multiplied: boolean;
        stackIndex: number;
        /** The library image, so a save from the viewer pins this picture. */
        id?: number;
        video?: ReactNode;
    }> = [];
    stack.forEach((slide, slideIndex) => {
        // A video takes its place in the viewer as a player, not as the still
        // poster the carousel shows. The same `GalleryVideoSlide` the overlay
        // plays is what a direct tap on the carousel row opens here.
        if (isLibraryVideo(slide)) {
            overlaySlides.push({
                src: getThumbnailImage(slide).image.Desktop ?? '',
                alt: slide.Title || product.Title,
                multiplied: false,
                stackIndex: slideIndex,
                video: <GalleryVideoSlide slide={slide} />,
            });
            return;
        }
        const src = slide.LibraryImageFile?.DetailPageNatural?.Desktop;
        if (src)
            overlaySlides.push({
                src,
                alt: product.Title,
                // Only the photographs carry one: a video or a diagram is not
                // a picture to pin, so a save from it keeps the product.
                id: getPhotoID(slide),
                // The overlay draws a clear-cut render the way the card here
                // does — contained on the neutral fill and multiplied into it —
                // so a white-on-white product doesn't disappear once it is
                // lifted off the grey card onto the darkened page.
                multiplied: isContainedSlide(slide),
                stackIndex: slideIndex,
            });
    });

    /**
     * Where a slide of the stack lands in the overlay's list. A slide with no
     * picture and no video behind it has no entry, so it has no answer here
     * either, and nothing can open the viewer on it.
     */
    const overlayIndexOfStackIndex = new Map(
        overlaySlides.map((slide, index) => [slide.stackIndex, index]),
    );
    const overlayIndex = overlayIndexOfStackIndex.get(activeIndex) ?? 0;

    /**
     * Keep the carousel on whatever the overlay is showing.
     *
     * Within the copy of the row the reader left it on: the track lays the
     * stack down `loopRepeats` times, so one stack index names a slide in every
     * copy, and always picking the first copy's would drag the row back to the
     * start behind the overlay.
     */
    const onOverlayIndexChange = (next: number) => {
        const target = overlaySlides[next];
        if (!target) return;
        setActiveIndex(target.stackIndex);
        const copyStart = currentSlide - (currentSlide % stack.length);
        emblaApi?.scrollTo(copyStart + target.stackIndex);
    };

    const openViewerAt = (index: number) => {
        trackViewerOpen();
        // The clicked slide is indexed in the rendered track, which is whole
        // repeats of the stack; the overlay is keyed by the original a copy
        // stands for, so fold the index back into the stack before storing it.
        setActiveIndex(index % stack.length);
        setViewerOpen(true);
    };

    /**
     * A click on a peeking slide is a request to see that image, not to zoom
     * the one already centred — so it recentres instead of opening the viewer.
     * Only a slide that is fully in view zooms: the settled centre slide, or
     * every slide once the row fits the viewport and nothing peeks.
     */
    const isCentredSlide = (index: number) =>
        trackFits || index === currentSlide;
    const handleSlideClick = (index: number, openViewer: () => void) => {
        if (!isCentredSlide(index)) {
            emblaApi?.scrollTo(index);
            return;
        }
        openViewer();
    };

    // Centre slide + both peeks are visible on load (the carousel loops, so
    // the last slide is the initial left peek); beyond that, eager-load two
    // slides either side of wherever the carousel has settled so neighbours
    // are painted before they're dragged into view.
    const isEagerSlide = (index: number) => {
        const count = renderedStack.length;
        if (index <= 1 || index === count - 1) return true;
        const distance = Math.min(
            Math.abs(index - currentSlide),
            count - Math.abs(index - currentSlide),
        );
        return distance <= 2;
    };

    if (singleClearCutWide && firstSlide && !isLibraryVideo(firstSlide)) {
        const heroSrc =
            firstSlide.LibraryImageFile?.DetailPageNatural?.Desktop ?? '';
        // Identical either side of the breakpoint split below — only the viewer
        // the surrounding button opens differs — so it's built once.
        const heroImage = (
            <Image
                src={heroSrc}
                alt={product.Title}
                className={cn(
                    heroUnderlaySrc && 'transition-opacity duration-300',
                    heroUnderlaySrc && !heroImageLoaded && 'opacity-0',
                )}
                onLoad={heroUnderlaySrc ? markHeroImageLoaded : undefined}
                fill
                fit="contain"
                multiplied
                sizes={PRODUCT_GALLERY_SLIDE_SIZES}
                loading="eager"
                priority
                fetchPriority="high"
                fadeIn={false}
            />
        );
        return (
            <section
                ref={islandRef}
                data-product-gallery
                className="relative w-full overflow-x-clip bg-ground"
            >
                {/* Single clear-cut hero: full-width band aligned to the page
                    content gutter (md:px-[3rem]); the product sits contained on
                    the neutral fill, which extends laterally to both gutters. */}
                <div className="md:px-[3rem]">
                    {/* Mobile stretches to the 1rem page gutter (matching the
                        carousel's lone-slide sizing); desktop extends to the
                        full content width. This band is what the carousel's
                        slide 0 is in the multi-image layout — the listing
                        tile's counterpart — so it carries the same shared
                        snapshot name and cold-cache underlay. */}
                    <div
                        // `group` reveals the action row on hover.
                        className="group relative mx-auto h-[84vw] w-[calc(100vw-2rem)] overflow-hidden rounded-tile bg-[var(--color-clearcut-fill)] md:mx-0 md:h-[54.375rem] md:w-full"
                        style={heroStyle}
                    >
                        {heroUnderlaySrc ? (
                            <img
                                src={heroUnderlaySrc}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 size-full object-contain mix-blend-multiply"
                            />
                        ) : null}
                        {mobile ? (
                            <button
                                type="button"
                                onClick={() => openViewerAt(0)}
                                className="absolute inset-0"
                                aria-label={`View ${product.Title} image 1 full screen`}
                            >
                                {heroImage}
                            </button>
                        ) : null}
                        <SlideBoost
                            slide={firstSlide}
                            disabled={mobile || isBMPreview}
                            onAutoplayPause={setBoostHold}
                        >
                            {mobile ? null : (
                                <button
                                    type="button"
                                    onClick={() => openViewerAt(0)}
                                    className="absolute inset-0 cursor-zoom-in"
                                    aria-label={`View ${product.Title} image 1 full screen`}
                                >
                                    {heroImage}
                                </button>
                            )}
                            {/* The same actions a carousel slide carries: an
                                always-visible button on touch, the hover row
                                on desktop. */}
                            <TileOverlayWithFollowSelector
                                {...getSlidePin(firstSlide, product.ID)}
                                libraryItemId={product.ID}
                                enquiry={photoEnquiry}
                                visitWebsite={photoVisitWebsite}
                                share={photoShare}
                                tileInfo={photoTileInfo}
                                shareLeading={<BoostActionButton />}
                                // The brand's logo badge hangs over this
                                // band's bottom-right corner, where Share
                                // would sit: the row stops a rem short of
                                // it (its 3.3125rem inset plus its 10.25rem
                                // disc).
                                footClassName="md:pr-[14.5625rem]"
                                pinterest={{
                                    mediaUrl:
                                        firstSlide.LibraryImageFile
                                            ?.DetailPageNatural?.Desktop ??
                                        undefined,
                                    professionalID: galleryProfessional.ID,
                                    libraryImageID: getPhotoID(firstSlide),
                                }}
                                mobileOnly={mobile}
                            />
                        </SlideBoost>

                        {/* Award badges, bottom-left inside the hero band
                            (flag-gated), matching the carousel gallery's
                            desktop-only placement. */}
                        {enableAwardBadges && product.Awards.length > 0 ? (
                            <div className="absolute z-10 max-md:hidden md:bottom-[1.44rem] md:left-[6.6rem]">
                                <BannerAwards
                                    awards={product.Awards}
                                    awardsPerSlide={5}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>

                {viewerOpen ? (
                    <Suspense fallback={null}>
                        <DetailImageOverlay
                            key={product.ID}
                            slides={overlaySlides}
                            index={overlayIndex}
                            onIndexChange={onOverlayIndexChange}
                            onClose={() => setViewerOpen(false)}
                            pinId={product.ID}
                            title={product.Title}
                            visitWebsite={photoVisitWebsite}
                        />
                    </Suspense>
                ) : null}
            </section>
        );
    }

    return (
        <section
            ref={islandRef}
            data-product-gallery
            className={cn(
                'relative w-full overflow-x-clip bg-ground',
                galleryGutter,
            )}
            style={
                {
                    '--g-slide-pad': `${GALLERY_SLIDE_PAD_REM}rem`,
                } as CSSProperties
            }
        >
            {/* Award badges, bottom-left inside the main image (flag-gated). */}
            {enableAwardBadges && product.Awards.length > 0 ? (
                <div className="absolute z-10 max-md:hidden md:top-[52.935rem] md:left-[9.6rem] md:-translate-y-full">
                    <BannerAwards awards={product.Awards} awardsPerSlide={5} />
                </div>
            ) : null}

            <Carousel
                setApi={setEmblaApi}
                ariaLabel={`${product.Title} photo gallery`}
                advanceMs={GALLERY_ADVANCE_MS}
                options={{
                    align: 'center',
                    // A row that fits the viewport has nowhere to scroll to:
                    // stand embla down so it stops centring the active slide
                    // (and stops dragging), leaving the CSS offset below to
                    // hold the whole row in the middle.
                    active: !carouselIdle,
                    // Slower, more deliberate slide (embla's scroll physics is a
                    // friction-based ease-out — see the easing note on the PR).
                    duration: 32,
                    // A bounded row of images ends where the images end: the
                    // first and last snaps rest against the page gutter rather
                    // than centring with empty track beside them. Slides in
                    // between still centre, and a lone slide has no row to trim.
                    containScroll: boundedRail && isMulti ? 'trimSnaps' : false,
                    startIndex: 0,
                    // Drag (and with it trackpad wheel gestures) stays on even
                    // while the island drives navigation — grabbing the gallery
                    // pauses the autoplay clock (pointerDown effect below), so
                    // the two no longer fight. Island mode keeps snap-drags so
                    // the island's active dot stays meaningful; without the
                    // island the original free-drag is kept.
                    watchDrag: true,
                    dragFree: !islandEnabled,
                    // A rail that wraps has no edge, and the edge is what the
                    // bounded row gives the reader to push against to learn
                    // there is nothing further — the browser rubber-bands the
                    // page there, as it does anywhere that has run out of
                    // sideways room. Autoplay stops on the last image either
                    // way rather than wrapping.
                    loop: isMulti && !boundedRail,
                    // Mobile aligns the row to the page gutter (first slide's
                    // left edge, last slide's right edge, via trimSnaps) with
                    // the next slide peeking; a lone slide just centres. Keeps
                    // native swipe — the island drives desktop, so drag is
                    // otherwise off there.
                    breakpoints: {
                        // .98, not 767: Tailwind's `md` starts at 768, so a
                        // fractional width in between would otherwise take
                        // embla's desktop centring while the slides still
                        // render at their mobile widths.
                        '(max-width: 767.98px)': {
                            align: 'start',
                            containScroll: isMulti ? 'trimSnaps' : false,
                            // No loop on mobile: a finite row so the last slide
                            // snaps to the right gutter (trimSnaps) instead of
                            // the first wrapping in with no trailing gap.
                            loop: false,
                            watchDrag: true,
                            // A swipe lands on a slide, never between two: a
                            // free-drag rail stopped wherever momentum ran out
                            // and left pictures cut in half at the gutter.
                            dragFree: false,
                        },
                    },
                }}
            >
                {/* Pre-centre slide 0 at SSR so the hero doesn't jump from
                    flush-left to centred when embla hydrates: embla's resting
                    transform for a centre-aligned slide 0 is exactly
                    (viewport - slide)/2, which this matches, so embla overrides
                    it with an identical value on init. Desktop only — mobile is
                    align:start, already flush-left correct. */}
                <CarouselContent
                    // The page gutter lives on the embla viewport, so the
                    // first and last snaps land on it while the slides in
                    // between scroll through it (see --g-pad). Mobile always
                    // has one; desktop only on the bounded rail, where the row
                    // has ends to rest against.
                    viewportClassName={
                        boundedRail
                            ? 'px-[var(--g-pad)]'
                            : 'max-md:px-[var(--g-pad)]'
                    }
                    className={cn(
                        'max-md:gap-3',
                        // A looping row rests at the offset that centres slide
                        // 0 (or the whole row, where it fits). A bounded row
                        // rests flush inside the page gutter, which is where
                        // the SSR markup already puts it — only a lone slide
                        // still centres, and for one slide the offset below is
                        // that same centring.
                        (!boundedRail || !isMulti) &&
                            'md:[transform:translateX(var(--g-offset))]',
                    )}
                    style={
                        {
                            '--g-offset': galleryTrackOffset(
                                firstSlideWidthRem,
                                trackWidthRem,
                            ),
                        } as CSSProperties
                    }
                >
                    {renderedStack.map((slide, index) => (
                        <CarouselItem
                            // Keyed by position: a boost swaps the slide's
                            // library image for a new one, and a key on its ID
                            // would remount the slide and lose the
                            // before/after mid-way.
                            key={index}
                            className="shrink-0 grow-0 basis-auto px-0 md:px-[var(--g-slide-pad)]"
                            style={
                                {
                                    '--g-slide-w': `${GALLERY_SLIDE_HEIGHT_REM * getSlideRatio(slide)}rem`,
                                    // A lone slide fills the width inside the
                                    // page gutter; clear-cut renders gain
                                    // canvas (object-contain), photos take a
                                    // mild cover-crop.
                                    '--g-slide-w-m': isMulti
                                        ? `${GALLERY_SLIDE_WIDTH_MOBILE_VW}vw`
                                        : 'calc(100vw - 2rem)',
                                } as CSSProperties
                            }
                        >
                            {/* Variable-width, consistent-height card: the
                                height is fixed and the width follows the image
                                ratio, so the row keeps one height. Square and
                                portrait fill their card; on mobile a landscape
                                is clamped to a square card and cropped to fill
                                (no letterbox). Cards sit a little inside the
                                viewport so the next slide peeks. */}
                            <div
                                // `group` is load-bearing: the hover panel
                                // below reveals on `group-hover`, so without it
                                // the actions sat at zero opacity for good and
                                // the gallery looked like it had none.
                                className="group relative h-[84vw] w-[var(--g-slide-w-m)] overflow-hidden rounded-tile bg-[var(--color-clearcut-fill)] md:h-[54.375rem] md:w-[var(--g-slide-w)]"
                                style={index === 0 ? heroStyle : undefined}
                            >
                                {index === 0 &&
                                heroUnderlaySrc &&
                                !isLibraryVideo(slide) ? (
                                    <img
                                        src={heroUnderlaySrc}
                                        alt=""
                                        aria-hidden="true"
                                        className={cn(
                                            'absolute inset-0 size-full',
                                            isContainedSlide(slide)
                                                ? 'object-contain mix-blend-multiply'
                                                : 'object-cover',
                                        )}
                                    />
                                ) : null}
                                <SlideBoost
                                    slide={slide}
                                    disabled={mobile || isBMPreview}
                                    onAutoplayPause={setBoostHold}
                                >
                                    {isLibraryVideo(slide) ? (
                                        <GalleryVideoSlideTrigger
                                            slide={slide}
                                            productTitle={product.Title}
                                            index={index}
                                            isCentred={
                                                mobile || isCentredSlide(index)
                                            }
                                            loading={
                                                isEagerSlide(index)
                                                    ? 'eager'
                                                    : 'lazy'
                                            }
                                            onActivate={() =>
                                                mobile
                                                    ? openViewerAt(index)
                                                    : handleSlideClick(
                                                          index,
                                                          () =>
                                                              openViewerAt(
                                                                  index,
                                                              ),
                                                      )
                                            }
                                        />
                                    ) : mobile ? (
                                        <button
                                            type="button"
                                            // Click-to-centre is a desktop
                                            // behaviour: this row is start-aligned
                                            // and free-dragging, so there is no
                                            // centre slot to move a slide into, and
                                            // routing the tap through it would cost
                                            // a second tap to open any image the
                                            // carousel had not settled on.
                                            onClick={() => openViewerAt(index)}
                                            className="absolute inset-0"
                                            aria-label={`View ${product.Title} image ${index + 1} full screen`}
                                        >
                                            <Image
                                                src={
                                                    slide.LibraryImageFile
                                                        ?.DetailPageNatural
                                                        ?.Desktop ?? ''
                                                }
                                                alt={product.Title}
                                                className={cn(
                                                    index === 0 &&
                                                        heroUnderlaySrc &&
                                                        'transition-opacity duration-300',
                                                    index === 0 &&
                                                        heroUnderlaySrc &&
                                                        !heroImageLoaded &&
                                                        'opacity-0',
                                                )}
                                                onLoad={
                                                    index === 0 &&
                                                    heroUnderlaySrc
                                                        ? markHeroImageLoaded
                                                        : undefined
                                                }
                                                fill
                                                // Photos fill the card (a landscape
                                                // crops to the square); clear-cut
                                                // product shots stay contained so the
                                                // whole product shows.
                                                fit={
                                                    isContainedSlide(slide)
                                                        ? 'contain'
                                                        : 'cover'
                                                }
                                                multiplied={isContainedSlide(
                                                    slide,
                                                )}
                                                sizes={
                                                    PRODUCT_GALLERY_SLIDE_SIZES
                                                }
                                                loading={
                                                    isEagerSlide(index)
                                                        ? 'eager'
                                                        : 'lazy'
                                                }
                                                priority={index === 0}
                                                fetchPriority={
                                                    index === 0
                                                        ? 'high'
                                                        : undefined
                                                }
                                                fadeIn={false}
                                            />
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleSlideClick(index, () =>
                                                    openViewerAt(index),
                                                )
                                            }
                                            className={cn(
                                                'absolute inset-0',
                                                isCentredSlide(index)
                                                    ? 'cursor-zoom-in'
                                                    : 'cursor-pointer',
                                            )}
                                            aria-label={
                                                isCentredSlide(index)
                                                    ? `View ${product.Title} image ${index + 1} full screen`
                                                    : `Bring ${product.Title} image ${index + 1} into view`
                                            }
                                        >
                                            {/* Clear-cut images (IsMultiplied) and
                                            diagrams sit contained on the grey
                                            fill and multiply into it, per the
                                            legacy gallery; photos keep the
                                            full-bleed crop. */}
                                            <Image
                                                src={
                                                    slide.LibraryImageFile
                                                        ?.DetailPageNatural
                                                        ?.Desktop ?? ''
                                                }
                                                alt={product.Title}
                                                className={cn(
                                                    index === 0 &&
                                                        heroUnderlaySrc &&
                                                        'transition-opacity duration-300',
                                                    index === 0 &&
                                                        heroUnderlaySrc &&
                                                        !heroImageLoaded &&
                                                        'opacity-0',
                                                )}
                                                onLoad={
                                                    index === 0 &&
                                                    heroUnderlaySrc
                                                        ? markHeroImageLoaded
                                                        : undefined
                                                }
                                                fill
                                                fit={
                                                    isContainedSlide(slide)
                                                        ? 'contain'
                                                        : 'cover'
                                                }
                                                multiplied={isContainedSlide(
                                                    slide,
                                                )}
                                                sizes={
                                                    PRODUCT_GALLERY_SLIDE_SIZES
                                                }
                                                loading={
                                                    isEagerSlide(index)
                                                        ? 'eager'
                                                        : 'lazy'
                                                }
                                                priority={index === 0}
                                                fetchPriority={
                                                    index === 0
                                                        ? 'high'
                                                        : undefined
                                                }
                                                fadeIn={false}
                                            />
                                        </button>
                                    )}
                                    {/* Save (design-board / FollowSelector) on the
                                    image. The comment here used to say desktop
                                    got the same actions from a hover overlay;
                                    it did not — the guard kept this to mobile,
                                    so the main gallery was the one set of
                                    images on the page with nothing on hover.
                                    `mobileOnly` still switches the treatment:
                                    an always-visible button on touch, where
                                    there is no hover to reveal it, and the
                                    hover overlay on desktop.

                                    Saves the specific image (PhotoPin, keyed
                                    by the LibraryImage id) rather than the
                                    product — the product itself is saved from
                                    the CTA group's DetailSaveButton. A diagram
                                    is the exception: it saves the product. */}
                                    {!isLibraryVideo(slide) ? (
                                        <TileOverlayWithFollowSelector
                                            {...getSlidePin(slide, product.ID)}
                                            // Boost Resolution, for staff, leads
                                            // the row Share sits in.
                                            shareLeading={<BoostActionButton />}
                                            libraryItemId={product.ID}
                                            enquiry={photoEnquiry}
                                            visitWebsite={photoVisitWebsite}
                                            share={photoShare}
                                            tileInfo={photoTileInfo}
                                            // The pin is of this slide, not of the
                                            // product: each photograph in the
                                            // gallery is its own picture, and the
                                            // panel is drawn per slide.
                                            pinterest={{
                                                mediaUrl:
                                                    slide.LibraryImageFile
                                                        ?.DetailPageNatural
                                                        ?.Desktop ?? undefined,
                                                professionalID:
                                                    galleryProfessional.ID,
                                                libraryImageID:
                                                    getPhotoID(slide),
                                            }}
                                            mobileOnly={mobile}
                                        />
                                    ) : null}
                                </SlideBoost>
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <GalleryPagination
                    trackFits={trackFits}
                    total={realSlideCount}
                    activeIndex={realCurrentSlide}
                />
            </Carousel>

            {viewerOpen ? (
                <Suspense fallback={null}>
                    <DetailImageOverlay
                        key={product.ID}
                        slides={overlaySlides}
                        index={overlayIndex}
                        onIndexChange={onOverlayIndexChange}
                        onClose={() => setViewerOpen(false)}
                        pinId={product.ID}
                        title={product.Title}
                        visitWebsite={photoVisitWebsite}
                    />
                </Suspense>
            ) : null}
        </section>
    );
};
